import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Establecimiento } from '../modelos/Establecimiento';
import type { Almacen } from '../modelos/Almacen';
import type { Serie } from '../modelos/Serie';
import type { PaymentMethod } from '../modelos/MetodoPago';
import type { FormaPago } from '../modelos/Configuracion';
import type { Unit } from '../modelos/Unidad';
import { mapearCategoriaDesdeBackend } from '../modelos/Unidad';
import type { Tax } from '../modelos/Impuesto';
import type { PreferenciasVenta } from '../components/Configuracion/Negocio/SeccionPreferenciasVenta';
import type { Categoria, InventarioUiData, UnidadMedida, AfectacionTributaria } from '../modelos/Configuracion';
import type { Currency } from '../modelos/Currency';
import type { Caja } from '../modelos/Caja';
import type { Rol, Permiso } from '../services/api/roles.service';
import type { Usuario } from '../services/api/usuarios.service';
import { categoriaService } from '../services/api/categorias.service';
import { unidadMedidaService } from '../services/api/unidadMedidas.service';
import { afectacionTributariaService } from '../services/api/afectacionTributarias.service';
import { useWorkspace } from './ContextoWorkspace';
import { storageService } from '../utils/storage';

interface ConfigurationState {
  Establecimientos: Establecimiento[];
  almacenes: Almacen[];
  usuarios: Usuario[];
  series: Serie[];
  cajas: Caja[];
  currencies: Currency[];
  roles: Rol[];
  permisos: Permiso[];
  paymentMethods: PaymentMethod[];
  formaPagos: FormaPago[];
  units: Unit[];
  taxes: Tax[];
  afectaciones: AfectacionTributaria[];
  categories: Categoria[];
  preferenciasVentas: PreferenciasVenta;
  preferenciasUi: InventarioUiData;
}

type ConfigurationAction =
  | { type: 'SET_EstablecimientoS'; payload: Establecimiento[] }
  | { type: 'ADD_Establecimiento'; payload: Establecimiento }
  | { type: 'UPDATE_Establecimiento'; payload: Establecimiento }
  | { type: 'DELETE_Establecimiento'; payload: string }
  | { type: 'SET_ALMACENES'; payload: Almacen[] }
  | { type: 'SET_USUARIOS'; payload: Usuario[] }
  | { type: 'SET_SERIES'; payload: Serie[] }
  | { type: 'SET_CAJAS'; payload: Caja[] }
  | { type: 'SET_CURRENCIES'; payload: Currency[] }
  | { type: 'SET_ROLES'; payload: Rol[] }
  | { type: 'SET_PERMISOS'; payload: Permiso[] }
  | { type: 'SET_PAYMENT_METHODS'; payload: PaymentMethod[] }
  | { type: 'SET_FORMA_PAGOS'; payload: FormaPago[] }
  | { type: 'SET_UNITS'; payload: Unit[] }
  | { type: 'SET_TAXES'; payload: Tax[] }
  | { type: 'SET_AFECTACIONES'; payload: AfectacionTributaria[] }
  | { type: 'SET_CATEGORIES'; payload: Categoria[] }
  | { type: 'SET_PREFERENCIAS_VENTAS'; payload: PreferenciasVenta }
  | { type: 'SET_PREFERENCIAS_UI'; payload: InventarioUiData };

interface ConfigurationContextType {
  state: ConfigurationState;
  dispatch: React.Dispatch<ConfigurationAction>;
}

const ConfigurationContext = createContext<ConfigurationContextType | undefined>(undefined);

const initialState: ConfigurationState = {
  Establecimientos: [],
  almacenes: [],
  usuarios: [],
  series: [],
  cajas: [],
  currencies: [],
  roles: [],
  permisos: [],
  paymentMethods: [],
  formaPagos: [],
  units: [],
  taxes: [],
  afectaciones: [],
  categories: [],
  preferenciasVentas: {
    permitirStockNegativo: false,
    preciosIncluyenIGV: true,
  },
  preferenciasUi: {},
};

function configurationReducer(
  state: ConfigurationState,
  action: ConfigurationAction
): ConfigurationState {
  switch (action.type) {
    case 'SET_EstablecimientoS':
      return {
        ...state,
        Establecimientos: action.payload
      };
    
    case 'ADD_Establecimiento':
      return {
        ...state,
        Establecimientos: [...state.Establecimientos, action.payload]
      };
    
    case 'UPDATE_Establecimiento':
      return {
        ...state,
        Establecimientos: state.Establecimientos.map(est =>
          est.id === action.payload.id ? action.payload : est
        )
      };
    
    case 'DELETE_Establecimiento':
      return {
        ...state,
        Establecimientos: state.Establecimientos.filter(est => est.id !== action.payload)
      };
    
    case 'SET_ALMACENES':
      return {
        ...state,
        almacenes: action.payload
      };
    
    case 'SET_USUARIOS':
      return {
        ...state,
        usuarios: action.payload
      };

    case 'SET_SERIES':
      return {
        ...state,
        series: action.payload
      };
    
    case 'SET_CAJAS':
      return {
        ...state,
        cajas: action.payload
      };

    case 'SET_CURRENCIES':
      return {
        ...state,
        currencies: action.payload
      };

    case 'SET_ROLES':
      return {
        ...state,
        roles: action.payload
      };

    case 'SET_PERMISOS':
      return {
        ...state,
        permisos: action.payload
      };

    case 'SET_PAYMENT_METHODS':
      return {
        ...state,
        paymentMethods: action.payload
      };

    case 'SET_FORMA_PAGOS':
      return {
        ...state,
        formaPagos: action.payload
      };

    case 'SET_UNITS':
      return {
        ...state,
        units: action.payload
      };
    
    case 'SET_TAXES':
      return {
        ...state,
        taxes: action.payload
      };

    case 'SET_AFECTACIONES':
      return {
        ...state,
        afectaciones: action.payload
      };

    case 'SET_CATEGORIES':
      return {
        ...state,
        categories: action.payload
      };
    
    case 'SET_PREFERENCIAS_VENTAS':
      return {
        ...state,
        preferenciasVentas: action.payload
      };

    case 'SET_PREFERENCIAS_UI':
      return {
        ...state,
        preferenciasUi: action.payload
      };

    default:
      return state;
  }
}

export function ConfigurationProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(configurationReducer, undefined, () => {
    const config = storageService.getConfiguracionActual();
    return {
      ...initialState,
      preferenciasVentas: {
        permitirStockNegativo: false,
        preciosIncluyenIGV: config?.tipoImpuesto !== 'no_incluye',
      },
    };
  });
  const { empresaId } = useWorkspace();
  const lastAfectacionesEmpresaIdRef = useRef<string | undefined>(undefined);
  const lastUnitsEmpresaIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!empresaId) return;

    const sincronizarDesdeStorage = () => {
      const config = storageService.getConfiguracionActual();
      const preciosIncluyenIGV = config?.tipoImpuesto !== 'no_incluye';

      const configInventarioStr =
        config?.configuracionInventario ??
        storageService.getConfiguracionByEmpresa(empresaId)?.configuracionInventario;

      let inventario: import('../modelos/Configuracion').ConfiguracionInventarioData | null = null;
      try {
        if (configInventarioStr) {
          inventario = JSON.parse(configInventarioStr);
        }
      } catch {
        inventario = null;
      }

      const configGeneralStr =
        config?.configuracionGeneral ??
        storageService.getConfiguracionByEmpresa(empresaId)?.configuracionGeneral;

      let general: import('../modelos/Configuracion').ConfiguracionGeneralData | null = null;
      try {
        if (configGeneralStr) {
          general = JSON.parse(configGeneralStr);
        }
      } catch {
        general = null;
      }

      dispatch({
        type: 'SET_PREFERENCIAS_VENTAS',
        payload: {
          permitirStockNegativo: inventario?.permitirStockNegativo ?? false,
          preciosIncluyenIGV,
          controlStockActivo: inventario?.controlStockActivo ?? false,
          allowNegativeStock: inventario?.permitirStockNegativo ?? false,
          stockDescuentoFacturaYBoleta: inventario?.stockDescuentoFacturaYBoleta ?? 'automatico',
          stockDescuentoNotaVenta: inventario?.stockDescuentoNotaVenta ?? 'sin_control',
          stockDescuentoGuiaRemision: inventario?.stockDescuentoGuiaRemision ?? 'automatico',
          stockIngresoComprobanteCompra: inventario?.stockIngresoComprobanteCompra ?? 'nota_ingreso',
          requiereAprobacionMovimientos: inventario?.requiereAprobacionMovimientos ?? false,
          permitirProductoDuplicado: general?.permitirProductoDuplicado ?? false,
          tipoComprobantePosPorDefecto: general?.tipoComprobantePosPorDefecto,
        },
      });

      const configInventarioUiStr =
        config?.inventarioUi ??
        storageService.getConfiguracionByEmpresa(empresaId)?.inventarioUi;

      let inventarioUi: InventarioUiData = {};
      try {
        if (configInventarioUiStr) {
          inventarioUi = JSON.parse(configInventarioUiStr);
        }
      } catch {
        inventarioUi = {};
      }

      dispatch({ type: 'SET_PREFERENCIAS_UI', payload: inventarioUi });
    };

    sincronizarDesdeStorage();

    window.addEventListener('senciyo:workspace-updated', sincronizarDesdeStorage);
    return () => window.removeEventListener('senciyo:workspace-updated', sincronizarDesdeStorage);
  }, [empresaId, dispatch]);

  useEffect(() => {
    if (state.currencies.length === 0) {
      const defaultCurrencies: Currency[] = [
        {
          id: 'PEN',
          code: 'PEN',
          symbol: 'S/',
          name: 'Sol Peruano',
          decimals: 2,
          isBaseCurrency: false,
          isActive: true
        },
        {
          id: 'USD',
          code: 'USD',
          symbol: '$',
          name: 'Dólar Americano',
          decimals: 2,
          isBaseCurrency: false,
          isActive: true
        }
      ];
      dispatch({ type: 'SET_CURRENCIES', payload: defaultCurrencies });
    }
  }, [state.currencies.length]);

  useEffect(() => {
    if (!empresaId || lastAfectacionesEmpresaIdRef.current === empresaId) return;
    lastAfectacionesEmpresaIdRef.current = empresaId;
    afectacionTributariaService.getAll(empresaId).then((res) => {
      if (res.data && Array.isArray(res.data)) {
        dispatch({ type: 'SET_AFECTACIONES', payload: res.data });
      }
    }).catch((err) => console.error('Error loading afectaciones:', err));
  }, [empresaId]);

  useEffect(() => {
    if (!empresaId) return;

    const loadCategories = async () => {
      try {
        const response = await categoriaService.getAll(empresaId);
        if (response.data && Array.isArray(response.data)) {
          dispatch({ type: 'SET_CATEGORIES', payload: response.data });
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategories();
  }, [empresaId]);

  useEffect(() => {
    if (!empresaId || lastUnitsEmpresaIdRef.current === empresaId) return;
    lastUnitsEmpresaIdRef.current = empresaId;

    const loadUnits = async () => {
      try {
        const response = await unidadMedidaService.getAll(empresaId);
        if (response.data && Array.isArray(response.data)) {
          const units = response.data.map((u: UnidadMedida) => ({
            id: u.id,
            code: u.codigoSunat,
            name: u.nombre,
            symbol: u.simbolo || u.codigoSunat,
            description: u.descripcion || u.nombre,
            category: mapearCategoriaDesdeBackend(u.categoriaCodigo),
            decimalPlaces: u.decimales ?? 2,
            isActive: true,
            isSystem: u.esSistema ?? false,
            isVisible: u.esVisible ?? true,
            isFavorite: u.esFavorito ?? false,
            displayOrder: u.ordenVisualizacion,
            createdAt: new Date(u.createdAt),
            updatedAt: new Date(u.updatedAt),
          }));
          dispatch({ type: 'SET_UNITS', payload: units });
        }
      } catch (error) {
        console.error('Error loading units:', error);
      }
    };

    loadUnits();
  }, [empresaId]);

  return (
    <ConfigurationContext.Provider value={{ state, dispatch }}>
      {children}
    </ConfigurationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfigurationContext() {
  const context = useContext(ConfigurationContext);
  if (context === undefined) {
    throw new Error('useConfigurationContext debe usarse dentro de ConfigurationProvider');
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfigurationContextSafe() {
  const context = useContext(ConfigurationContext);
  return context ?? { state: initialState, dispatch: () => {} };
}
